/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/** @jsxImportSource solid-js */

import {
  Component,
  createSignal,
  createEffect,
  createMemo,
  onCleanup,
  Show,
  on,
} from 'solid-js';
import { classNames } from '@fulcrum/lib';

export interface ISnackBarMessage {
  text: string;
  type?: IColors;
  timeoutMilliseconds?: number;
  onClick?(): void;
  buttonText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callBack?(args?: any): void;
}

export const staticColors = {
  success: '#5E9331',
  error: '#C81922',
  warning: '#FFBE00',
  info: '#363545',
};

export type IColors = 'success' | 'error' | 'warning' | 'info';

export interface ConnectSnackbarProps {
  message?: ISnackBarMessage;
  color?: string;
}

const ConnectSnackbar: Component<ConnectSnackbarProps> = (props) => {
  const defaultTimeOut = 3200;
  const [visible, setVisible] = createSignal<boolean>(false);
  const [timer, setTimer] = createSignal<number>(0);

  const cancelTimeout = () => {
    const currentTimer = timer();
    if (currentTimer) {
      window.clearTimeout(currentTimer);
      setTimer(0);
    }
  };

  const showSnackbar = (timeout?: number) => {
    cancelTimeout();
    setVisible(true);
    const timeoutId = window.setTimeout(
      hideSnackbar,
      timeout ? timeout : defaultTimeOut
    );
    setTimer(timeoutId);
  };

  const hideSnackbar = () => {
    cancelTimeout();
    if (props.message && props.message.callBack) {
      props.message.callBack();
    }
    setVisible(false);
  };

  const onClickMessage = () => {
    hideSnackbar();

    if (props.message && props.message.onClick) {
      props.message.onClick();
    }
  };

  // Effect to handle message changes - track only text to prevent infinite loops
  createEffect(
    on(
      () => {
        return props.message?.text;
      }, // Only track text changes
      (text) => {
        if (text && props.message) {
          showSnackbar(props.message.timeoutMilliseconds);
        } else {
          hideSnackbar();
        }
      }
    )
  );

  // Cleanup on component unmount
  onCleanup(() => {
    cancelTimeout();
  });

  const getAnimationDelay = () => {
    const timeout = props.message
      ? props.message.timeoutMilliseconds
      : undefined;
    return timeout ? `0s, ${((timeout - 200) / 1000).toString()}s` : '';
  };

  const snackbarStyle = createMemo(() => {
    const style: Record<string, string> = {};

    // Determine color from message type or use explicit color prop
    const backgroundColor =
      props.color ||
      (props.message?.type ? staticColors[props.message.type] : undefined);

    if (backgroundColor) {
      style['background-color'] = backgroundColor;
    }

    if (visible()) {
      const animationDelay = getAnimationDelay();
      style['animation-delay'] = animationDelay;
      style['Webkit-animation-delay'] = animationDelay;
    }
    style['position'] = 'sticky';
    return style;
  });

  return (
    <Show when={props.message}>
      <div
        onClick={onClickMessage}
        class={classNames(
          'snackbar',
          { visible: visible() },
          { buttonless: props.message?.buttonText === undefined }
        )}
        style={snackbarStyle()}
      >
        <div class="d-flex center">
          <label data-cy="snackbarMessage">{props.message?.text}</label>
          <Show when={props.message?.buttonText && props.message.callBack}>
            <button
              class="button primary ml-1"
              onClick={() => props.message?.callBack?.()}
            >
              {props.message?.buttonText}
            </button>
          </Show>
        </div>
      </div>
    </Show>
  );
};

export default ConnectSnackbar;
